/**
 * WidgetRenderer - Universal widget renderer
 *
 * Handles two modes:
 * - Realtime: config has `api` + `params` → calls API → renders with fresh data
 * - Static: config has `artifact_id` → fetches from DB → renders
 * - Hybrid: artifact has `api` field → fetch artifact, then call API
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { isRealtimeWidget, isStaticWidget, getWidgetMode } from '../../utils/widgetParser';
import {
    callWidgetApi as callWidgetApiUtil,
    fetchArtifact as fetchArtifactUtil,
    fetchWidgetArtifact as fetchWidgetArtifactUtil
} from '../../utils/widgetApi';

// Widget Registry - maps type to component
import StockInfoCard from './StockInfoCard';
// PriceChartWidget deprecated - now uses StockInfoCard
import StockOverviewWidget from './StockOverviewWidget';
import NewsFeedWidget from './NewsFeedWidget';
import AnalysisReportWidget from './AnalysisReportWidget';
import FinancialChartWidget from './FinancialChartWidget';
import PeerComparisonWidget from './PeerComparisonWidget';
import ValuationWidget from './ValuationWidget';
import NebulaRecommendWidget from './NebulaRecommendWidget';
import PortfolioWidget from './PortfolioWidget';
import ChartWidget from './ChartWidget';
import InfoCardWidget from './InfoCardWidget';
import GenericWidget from './GenericWidget';

const WIDGET_REGISTRY = {
    stock_info: StockInfoCard,
    price_chart: StockInfoCard, // Deprecated: now uses same widget as stock_info
    stock_overview: StockOverviewWidget,
    news_feed: NewsFeedWidget,
    analysis_report: AnalysisReportWidget,
    financial_chart: FinancialChartWidget,
    peer_comparison: PeerComparisonWidget,
    valuation: ValuationWidget,
    nebula_recommend: NebulaRecommendWidget,
    portfolio_overview: PortfolioWidget,
    chart: ChartWidget, // v2.0 Highcharts with hybrid data_source support
    info_card: InfoCardWidget, // Client-only: uses config.params directly
};

// Type to API mapping - client resolves API from widget type
const TYPE_TO_API = {
    stock_info: 'get_ticker_info',
    price_chart: 'get_ticker_info', // Deprecated: now uses same API as stock_info
    stock_overview: 'get_ticker_info',
    news_feed: 'get_news',
    analysis_report: 'get_analysis_reports',
    financial_chart: 'get_financial_reports',
    peer_comparison: 'get_peer_comparison',
    valuation: 'get_valuation_analysis',
    nebula_recommend: 'get_nebula_stats',
    portfolio_overview: 'get_portfolio_overview',
};

const API_TO_TYPE = Object.fromEntries(
    Object.entries(TYPE_TO_API).map(([type, api]) => [api, type])
);

/**
 * Resolve API from widget config
 * Supports both: {api: 'xxx'} (legacy) and {type: 'xxx'} (new)
 */
function resolveApi(config) {
    if (config.api) return config.api;
    if (config.type && TYPE_TO_API[config.type]) {
        return TYPE_TO_API[config.type];
    }
    return null;
}

function resolveType(config) {
    if (config.type) return config.type;
    if (config.api && API_TO_TYPE[config.api]) return API_TO_TYPE[config.api];
    return null;
}

// Use centralized widget API utilities
const fetchArtifact = fetchArtifactUtil;
const fetchWidgetArtifact = fetchWidgetArtifactUtil;
const callWidgetApi = callWidgetApiUtil;

/**
 * Check if artifact is a v2.0 chart (uses fetchWidgetArtifact for realtime resolution)
 */
function isChartArtifact(artifactId) {
    return artifactId && artifactId.startsWith('widget_');
}

/**
 * Widget skeleton loader
 */
function WidgetSkeleton({ type }) {
    const skeletonClass = "animate-pulse bg-gray-200 dark:bg-gray-700 rounded";

    if (type === 'stock_info') {
        return (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                {/* Header skeleton */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${skeletonClass} rounded-full`} />
                        <div className="flex-1">
                            <div className={`h-5 w-24 ${skeletonClass} mb-2`} />
                            <div className={`h-4 w-32 ${skeletonClass}`} />
                        </div>
                        <div className="text-right">
                            <div className={`h-7 w-24 ${skeletonClass} mb-1`} />
                            <div className={`h-4 w-16 ${skeletonClass}`} />
                        </div>
                    </div>
                </div>
                {/* Time range selector skeleton */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex gap-1">
                        {[1, 2, 3, 4, 5, 6, 7].map(i => (
                            <div key={i} className={`h-6 w-8 ${skeletonClass}`} />
                        ))}
                    </div>
                    <div className="flex gap-1">
                        <div className={`h-7 w-7 ${skeletonClass}`} />
                        <div className={`h-7 w-7 ${skeletonClass}`} />
                    </div>
                </div>
                {/* Chart skeleton */}
                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className={`h-64 w-full ${skeletonClass}`} />
                </div>
                {/* Metrics skeleton - 3 columns */}
                <div className="px-4 py-3">
                    <div className="grid grid-cols-3 gap-x-6 gap-y-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                            <div key={i} className="flex justify-between">
                                <div className={`h-3 w-12 ${skeletonClass}`} />
                                <div className={`h-3 w-14 ${skeletonClass}`} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (type === 'price_chart' || type === 'chart') {
        return (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <div className={`h-5 w-32 ${skeletonClass}`} />
                </div>
                <div className="p-4">
                    <div className={`h-[350px] w-full ${skeletonClass}`} />
                </div>
            </div>
        );
    }

    if (type === 'news_feed') {
        // Slider-style skeleton (matching SimpleNewsSlider layout)
        return (
            <div className="flex gap-3 overflow-hidden">
                {[1, 2, 3].map(i => (
                    <div
                        key={i}
                        className="flex-shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden news-item"
                    >
                        {/* Image skeleton */}
                        <div className={`w-full h-[100px] sm:h-[157px] ${skeletonClass}`} />
                        {/* Content skeleton */}
                        <div className="p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <div className={`w-4 h-4 ${skeletonClass} rounded-full`} />
                                <div className={`h-3 w-16 ${skeletonClass}`} />
                            </div>
                            <div className={`h-4 w-full ${skeletonClass}`} />
                            <div className={`h-4 w-3/4 ${skeletonClass}`} />
                            <div className={`h-3 w-12 ${skeletonClass}`} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (type === 'analysis_report') {
        // Analysis report slider skeleton
        return (
            <div className="flex gap-3 overflow-hidden">
                {[1, 2, 3].map(i => (
                    <div
                        key={i}
                        className="flex-shrink-0 w-[280px] border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                    >
                        <div className="p-4 space-y-3">
                            {/* Header: ticker + recommendation */}
                            <div className="flex items-center justify-between">
                                <div className={`h-6 w-16 ${skeletonClass}`} />
                                <div className={`h-6 w-12 ${skeletonClass} rounded-full`} />
                            </div>
                            {/* Title */}
                            <div className={`h-4 w-full ${skeletonClass}`} />
                            <div className={`h-4 w-3/4 ${skeletonClass}`} />
                            {/* Price info */}
                            <div className="flex justify-between">
                                <div className={`h-5 w-24 ${skeletonClass}`} />
                                <div className={`h-5 w-20 ${skeletonClass}`} />
                            </div>
                            {/* Footer: source + link */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                                <div className={`h-4 w-16 ${skeletonClass}`} />
                                <div className={`h-4 w-20 ${skeletonClass}`} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (type === 'financial_chart') {
        // Financial chart skeleton
        return (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className={`h-6 w-48 ${skeletonClass}`} />
                        <div className="flex gap-2">
                            <div className={`h-8 w-16 ${skeletonClass}`} />
                            <div className={`h-8 w-16 ${skeletonClass}`} />
                        </div>
                    </div>
                </div>
                {/* Chart area */}
                <div className="p-4">
                    <div className={`h-[300px] w-full ${skeletonClass}`} />
                </div>
                {/* Legend */}
                <div className="px-4 pb-4 flex gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center gap-2">
                            <div className={`h-3 w-3 ${skeletonClass} rounded-full`} />
                            <div className={`h-3 w-20 ${skeletonClass}`} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (type === 'peer_comparison') {
        // Peer comparison skeleton
        return (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                        <div className={`h-6 w-14 ${skeletonClass} rounded`} />
                        <div className={`h-4 w-32 ${skeletonClass}`} />
                    </div>
                </div>
                {/* Charts grid */}
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[1, 2].map(i => (
                        <div key={i}>
                            <div className={`h-4 w-20 ${skeletonClass} mx-auto mb-4`} />
                            <div className={`h-[200px] w-full ${skeletonClass}`} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (type === 'valuation') {
        // Valuation gauge skeleton
        return (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex justify-between">
                        <div>
                            <div className={`h-4 w-24 ${skeletonClass} mb-2`} />
                            <div className={`h-8 w-32 ${skeletonClass} mb-1`} />
                            <div className={`h-3 w-20 ${skeletonClass}`} />
                        </div>
                        <div className="text-right">
                            <div className={`h-4 w-12 ${skeletonClass} mb-1`} />
                            <div className={`h-3 w-24 ${skeletonClass}`} />
                        </div>
                    </div>
                </div>
                {/* Gauge bar */}
                <div className="p-4">
                    <div className={`h-12 w-full ${skeletonClass} rounded-lg`} />
                </div>
                {/* Legend */}
                <div className="px-4 pb-3 flex justify-center gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center gap-2">
                            <div className={`h-3 w-3 ${skeletonClass} rounded`} />
                            <div className={`h-3 w-16 ${skeletonClass}`} />
                        </div>
                    ))}
                </div>
                {/* Notes */}
                <div className="px-4 pb-4 space-y-2">
                    <div className={`h-4 w-full ${skeletonClass}`} />
                    <div className={`h-4 w-3/4 ${skeletonClass}`} />
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className={`h-32 w-full ${skeletonClass}`} />
        </div>
    );
}

/**
 * Widget error display
 */
function WidgetError({ error, onRetry }) {
    return (
        <div className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Widget Error</span>
            </div>
            <p className="mt-2 text-sm text-red-500 dark:text-red-400">{error}</p>
            {onRetry && (
                <button
                    onClick={onRetry}
                    className="mt-3 px-3 py-1 text-sm bg-red-100 dark:bg-red-800 rounded hover:bg-red-200 dark:hover:bg-red-700"
                >
                    Retry
                </button>
            )}
        </div>
    );
}

/**
 * Main WidgetRenderer component
 */
function isKnownWidget(config) {
    if (config.type && (WIDGET_REGISTRY[config.type] || TYPE_TO_API[config.type])) return true;
    if (config.api && API_TO_TYPE[config.api]) return true;
    return false;
}

export default function WidgetRenderer({ config }) {
    const known = useMemo(() => isKnownWidget(config), [config]);
    const [widgetData, setWidgetData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    const widgetMode = useMemo(() => getWidgetMode(config), [config]);

    // Stable config key to prevent re-fetching on parent re-renders
    const configKey = useMemo(() => JSON.stringify(config), [config]);

    // Request ID to handle race conditions
    const requestIdRef = useRef(0);
    const isLoadingRef = useRef(false);

    useEffect(() => {
        // Skip if already loading (prevent duplicate calls)
        if (isLoadingRef.current) return;

        const currentRequestId = ++requestIdRef.current;
        isLoadingRef.current = true;

        async function loadWidget() {
            setLoading(true);
            setError(null);

            try {
                if (isStaticWidget(config)) {
                    // ARTIFACT MODE: Fetch from DB

                    // v2.0 Chart artifacts (widget_*) use special endpoint with realtime resolution
                    if (isChartArtifact(config.artifact_id)) {
                        // CHART ARTIFACT: Use fetchWidgetArtifact which resolves realtime series
                        const artifact = await fetchWidgetArtifact(config.artifact_id, true);

                        if (currentRequestId !== requestIdRef.current) return;

                        // artifact.data contains resolved chart spec (realtime series populated)
                        setWidgetData({
                            type: artifact.type || 'chart',
                            artifact_id: config.artifact_id,
                            ...artifact,
                            data: artifact.data
                        });
                    } else {
                        // LEGACY: Use fetchArtifact for non-chart artifacts
                        const artifact = await fetchArtifact(config.artifact_id);

                        if (currentRequestId !== requestIdRef.current) return;

                        if (artifact.api) {
                            // HYBRID: Artifact has API config → Call API for main data
                            const apiData = await callWidgetApi(artifact.api, artifact.params);

                            if (currentRequestId !== requestIdRef.current) return;

                            // Merge: API data + static overlays/annotations from artifact
                            setWidgetData({
                                type: artifact.type || artifact.widget_type,
                                ...artifact,
                                data: apiData.data,
                                overlays: artifact.overlays,
                                annotations: artifact.annotations
                            });
                        } else {
                            // PURE STATIC: All data in artifact
                            setWidgetData({
                                type: artifact.type || artifact.widget_type,
                                ...artifact
                            });
                        }
                    }
                } else if (isRealtimeWidget(config)) {
                    const api = resolveApi(config);
                    const widgetType = resolveType(config);

                    if (!api && WIDGET_REGISTRY[config.type]) {
                        // CLIENT-ONLY: type in registry but no API → use params as data
                        if (currentRequestId !== requestIdRef.current) return;

                        setWidgetData({
                            type: widgetType,
                            ...config,
                            data: config.params || {},
                        });
                    } else if (api) {
                        // REALTIME MODE: Call backend proxy API
                        const apiData = await callWidgetApi(api, config.params);

                        if (currentRequestId !== requestIdRef.current) return;

                        setWidgetData({
                            type: widgetType,
                            ...config,
                            data: apiData.data
                        });
                    } else {
                        throw new Error(`Unknown widget type: ${config.type}`);
                    }
                } else {
                    throw new Error('Invalid widget config: missing type or artifact_id');
                }
            } catch (err) {
                if (currentRequestId === requestIdRef.current) {
                    console.error('Widget load error:', err);
                    setError(err.message);
                }
            } finally {
                if (currentRequestId === requestIdRef.current) {
                    isLoadingRef.current = false;
                    setLoading(false);
                }
            }
        }

        loadWidget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configKey, retryCount]);

    const handleRetry = () => {
        isLoadingRef.current = false;
        setRetryCount(prev => prev + 1);
    };

    if (!known || error) {
        return null;
    }

    if (loading) {
        return <WidgetSkeleton type={config.type} />;
    }

    // No data
    if (!widgetData) {
        return null;
    }

    // Get component from registry
    const Component = WIDGET_REGISTRY[widgetData.type] || GenericWidget;

    return (
        <div className="my-3">
            <Component
                data={widgetData.data}
                config={widgetData}
                mode={widgetMode}
            />
        </div>
    );
}

// Re-export for convenience
export { WIDGET_REGISTRY, WidgetSkeleton, WidgetError };
